import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

import { useAuth } from '../../context/AuthContext';

const VendorRegister = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const { registerVendor, isLoading } = useAuth();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors }, watch } = useForm();

  const categories = [
    'إلكترونيات',
    'ملابس',
    'طعام ومشروبات',
    'كتب وقرطاسية',
    'صحة وجمال',
    'رياضة',
    'منزل وحديقة',
    'سيارات',
    'أخرى'
  ];

  const onSubmit = async (data) => {
    const vendorData = {
      // بيانات المستخدم
      name: data.name,
      email: data.email,
      password: data.password,
      phone: data.phone,
      
      // بيانات المتجر
      storeName: data.storeName,
      storeSlug: data.storeSlug,
      description: data.description,
      category: data.category,
      
      // بيانات الأعمال
      businessLicense: data.businessLicense,
      taxNumber: data.taxNumber,
      
      // بيانات البنك
      bankAccount: {
        bankName: data.bankName,
        accountNumber: data.accountNumber,
        accountName: data.accountName
      }
    };

    const result = await registerVendor(vendorData);
    
    if (result.success) {
      toast.success('تم تسجيل طلب البائع بنجاح! سيتم مراجعة طلبك خلال 24-48 ساعة.');
      navigate('/vendor/dashboard');
    } else {
      toast.error(result.message);
    }
  };

  const nextStep = () => setCurrentStep(currentStep + 1);
  const prevStep = () => setCurrentStep(currentStep - 1);

  // تحويل اسم المتجر إلى slug
  const generateSlug = (storeName) => {
    return storeName
      .toLowerCase()
      .replace(/[^a-z0-9\u0600-\u06FF\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div>
          <div className="mx-auto flex justify-center">
            <img 
              src="/logo.png" 
              alt="أمازول" 
              className="h-16 w-auto"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            انضم كبائع
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            ابدأ رحلتك التجارية معنا واعرض منتجاتك لآلاف العملاء
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center">
          <div className="flex items-center space-x-4 space-x-reverse">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step <= currentStep
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step}
                </div>
                {step < 3 && (
                  <div
                    className={`w-12 h-1 mx-2 ${
                      step < currentStep ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6">
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                المعلومات الشخصية
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">الاسم الكامل</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="أدخل اسمك الكامل"
                    {...register('name', {
                      required: 'الاسم مطلوب',
                      minLength: { value: 2, message: 'الاسم يجب أن يكون حرفين على الأقل' }
                    })}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">رقم الهاتف</label>
                  <input
                    type="tel"
                    className="form-input"
                    placeholder="مثال: 0123456789"
                    {...register('phone', {
                      required: 'رقم الهاتف مطلوب',
                      pattern: {
                        value: /^(\+249|0)?[0-9]{9}$/,
                        message: 'رقم الهاتف غير صحيح'
                      }
                    })}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="form-label">البريد الإلكتروني</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="أدخل بريدك الإلكتروني"
                  {...register('email', {
                    required: 'البريد الإلكتروني مطلوب',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'البريد الإلكتروني غير صحيح'
                    }
                  })}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">كلمة المرور</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input pl-10"
                    placeholder="أدخل كلمة المرور"
                    {...register('password', {
                      required: 'كلمة المرور مطلوبة',
                      minLength: {
                        value: 6,
                        message: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل'
                      }
                    })}
                  />
                  <button
                    type="button"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Store Information */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                معلومات المتجر
              </h3>
              
              <div>
                <label className="form-label">اسم المتجر</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="أدخل اسم متجرك"
                  {...register('storeName', {
                    required: 'اسم المتجر مطلوب',
                    minLength: { value: 2, message: 'اسم المتجر يجب أن يكون حرفين على الأقل' },
                    onChange: (e) => {
                      const slug = generateSlug(e.target.value);
                      // تحديث قيمة الـ slug
                      document.querySelector('input[name="storeSlug"]').value = slug;
                    }
                  })}
                />
                {errors.storeName && (
                  <p className="text-red-500 text-sm mt-1">{errors.storeName.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">رابط المتجر</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                    sudanmarket.com/store/
                  </span>
                  <input
                    type="text"
                    className="form-input rounded-r-none"
                    placeholder="my-store"
                    {...register('storeSlug', {
                      required: 'رابط المتجر مطلوب',
                      pattern: {
                        value: /^[a-z0-9-]+$/,
                        message: 'رابط المتجر يجب أن يحتوي على أحرف إنجليزية وأرقام وشرطات فقط'
                      }
                    })}
                  />
                </div>
                {errors.storeSlug && (
                  <p className="text-red-500 text-sm mt-1">{errors.storeSlug.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">فئة المتجر</label>
                <select
                  className="form-input"
                  {...register('category', { required: 'فئة المتجر مطلوبة' })}
                >
                  <option value="">اختر فئة المتجر</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">وصف المتجر (اختياري)</label>
                <textarea
                  rows={3}
                  className="form-input"
                  placeholder="اكتب وصفاً مختصراً عن متجرك ومنتجاتك"
                  {...register('description')}
                />
              </div>
            </div>
          )}

          {/* Step 3: Business Information */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                معلومات الأعمال والبنك
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">رقم الترخيص التجاري</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="أدخل رقم الترخيص"
                    {...register('businessLicense', {
                      required: 'رقم الترخيص التجاري مطلوب'
                    })}
                  />
                  {errors.businessLicense && (
                    <p className="text-red-500 text-sm mt-1">{errors.businessLicense.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">الرقم الضريبي</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="أدخل الرقم الضريبي"
                    {...register('taxNumber', {
                      required: 'الرقم الضريبي مطلوب'
                    })}
                  />
                  {errors.taxNumber && (
                    <p className="text-red-500 text-sm mt-1">{errors.taxNumber.message}</p>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">
                  معلومات الحساب البنكي
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="form-label">اسم البنك</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="مثال: بنك الخرطوم"
                      {...register('bankName', {
                        required: 'اسم البنك مطلوب'
                      })}
                    />
                    {errors.bankName && (
                      <p className="text-red-500 text-sm mt-1">{errors.bankName.message}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">رقم الحساب</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="أدخل رقم الحساب"
                        {...register('accountNumber', {
                          required: 'رقم الحساب مطلوب'
                        })}
                      />
                      {errors.accountNumber && (
                        <p className="text-red-500 text-sm mt-1">{errors.accountNumber.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">اسم صاحب الحساب</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="اسم صاحب الحساب"
                        {...register('accountName', {
                          required: 'اسم صاحب الحساب مطلوب'
                        })}
                      />
                      {errors.accountName && (
                        <p className="text-red-500 text-sm mt-1">{errors.accountName.message}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="btn-outline"
              >
                السابق
              </button>
            )}
            
            <div className="flex-1" />
            
            {currentStep < 3 ? (
              <button
                type="button"
                onClick={nextStep}
                className="btn-primary"
              >
                التالي
              </button>
            ) : (
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'جاري التسجيل...' : 'إرسال الطلب'}
              </button>
            )}
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              لديك حساب بالفعل؟{' '}
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                تسجيل الدخول
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorRegister;